import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Gift, X, Music2, Mic2, Disc3, Trophy, Crown, Loader2, Wallet as WalletIcon } from "lucide-react";
import { toast } from "sonner";
import { tr } from "@/lib/translate";

const ICONS = { musical_note: Music2, microphone: Mic2, gold_disc: Disc3, grammy: Trophy, legend: Crown };
const COLORS = {
  musical_note: "#00F2FE",
  microphone: "#FF0050",
  gold_disc: "#FFD60A",
  grammy: "#8A2BE2",
  legend: "linear-gradient(135deg,#FFD60A,#FF0050,#8A2BE2)",
};

/** Floating animated gifts received during a live (polls every 3s).
 *  The artist can't send themselves a gift, so on their own live this stays
 *  a discreet toast notification instead of the big full-screen animation
 *  fans see (isOwner). */
export function GiftOverlay({ liveId, isOwner }) {
  const [items, setItems] = useState([]);
  const [lastSeen, setLastSeen] = useState(new Date().toISOString());

  useEffect(() => {
    let mounted = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/lives/${liveId}/gifts`, { params: { since: lastSeen, limit: 20 } });
        if (!mounted || !data.length) return;
        setLastSeen(data[data.length - 1].created_at);
        if (isOwner) {
          data.forEach((g) => toast(`${g.gift_emoji} ${g.user_name} a envoyé ${g.gift_label}`, { duration: 3000 }));
          return;
        }
        data.forEach((g, i) => {
          setTimeout(() => {
            setItems((prev) => [...prev, g]);
            const ttl = g.gift_id === "legend" ? 7000 : g.gift_id === "grammy" ? 5500 : 4500;
            setTimeout(() => setItems((prev) => prev.filter((x) => x.id !== g.id)), ttl);
          }, i * 350);
        });
      } catch { /* silent */ }
    };
    const id = setInterval(poll, 3000);
    return () => { mounted = false; clearInterval(id); };
  }, [liveId, lastSeen, isOwner]);

  if (isOwner) return null;
  return (
    <div className="pointer-events-none absolute inset-0 z-30 overflow-hidden" data-testid="gift-overlay">
      {items.map((g) => <GiftBubble key={g.id} g={g} />)}
    </div>
  );
}

function GiftBubble({ g }) {
  const Icon = ICONS[g.gift_id] || Gift;
  const cls = `gift-${g.gift_anim}`;
  return (
    <div
      className={`absolute left-1/2 bottom-10 -translate-x-1/2 px-4 py-3 rounded-2xl bg-black/70 backdrop-blur-md border border-white/15 shadow-2xl text-center anim-in ${cls}`}
      data-testid={`gift-bubble-${g.gift_id}`}
    >
      <div className="flex items-center gap-2 justify-center">
        <Icon className="w-6 h-6" style={{ color: typeof COLORS[g.gift_id] === "string" && COLORS[g.gift_id].startsWith("#") ? COLORS[g.gift_id] : "#FFD60A" }} />
        <span className="text-2xl">{g.gift_emoji}</span>
      </div>
      <div className="mt-1.5">
        <div className="text-xs font-bold text-white">{g.user_name}</div>
        <div className="text-[11px] text-zinc-300">{g.gift_label} · {g.notes_cost} Notes</div>
        {g.message && <div className="text-[11px] text-yellow-200 mt-0.5 max-w-[200px] truncate">{g.message}</div>}
      </div>
    </div>
  );
}

/** Wallet & gift sender button → modal */
export function GiftButton({ liveId, artistName, isOwner, onSent }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  if (!user || isOwner) return null;
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="absolute right-3 bottom-20 z-20 w-14 h-14 rounded-full bg-gradient-to-br from-[#FFD60A] to-[#FF0050] grid place-items-center shadow-[0_10px_30px_rgba(255,214,10,0.45)] active:scale-95 transition-transform"
        data-testid="open-gift-btn"
        aria-label="Send a gift"
      >
        <Gift className="w-6 h-6 text-black" fill="currentColor" />
      </button>
      {open && <GiftModal liveId={liveId} artistName={artistName} onClose={() => setOpen(false)} onSent={onSent} />}
    </>
  );
}

function GiftModal({ liveId, artistName, onClose, onSent }) {
  const [catalog, setCatalog] = useState(null);
  const [wallet, setWallet] = useState({ notes: 0 });
  const [bundles, setBundles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("send"); // send | recharge

  useEffect(() => {
    (async () => {
      const [c, w] = await Promise.all([api.get("/notes/catalog"), api.get("/notes/wallet")]);
      setCatalog(c.data);
      setBundles(c.data.bundles);
      setWallet(w.data);
    })();
  }, []);

  const sendGift = async (gift) => {
    if ((wallet.notes || 0) < gift.notes) {
      toast.error("Pas assez de Notes — rechargez");
      setTab("recharge");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/lives/${liveId}/gifts`, { gift_id: gift.id });
      toast.success(`${gift.emoji} ${gift.label} envoyé !`);
      setWallet((w) => ({ ...w, notes: (w.notes || 0) - gift.notes }));
      onSent && onSent(data);
    } catch (e) {
      toast.error(tr(e.response?.data?.detail || "Erreur"));
    } finally { setBusy(false); }
  };

  const buyBundle = async (bundle) => {
    setBusy(true);
    try {
      const { data } = await api.post("/notes/purchase", { bundle_id: bundle.id, method: "stripe" });
      setWallet((w) => ({ ...w, notes: data.wallet.notes }));
      toast.success(`+${bundle.notes + (bundle.bonus || 0)} Notes`);
      setTab("send");
    } catch (e) { toast.error(tr(e.response?.data?.detail || "Erreur")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/85 backdrop-blur-xl grid place-items-center p-4" data-testid="gift-modal">
      <div className="w-full max-w-md bg-zinc-950 border border-white/10 rounded-3xl p-5 anim-in relative max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full bg-white/5 hover:bg-white/10" data-testid="gift-close">
          <X className="w-4 h-4 text-zinc-400" />
        </button>
        <div className="flex items-center gap-2">
          <Gift className="w-5 h-5 text-[#FFD60A]" />
          <h3 className="text-xl font-black font-display text-white">Cadeau musical</h3>
        </div>
        <p className="text-xs text-zinc-400">pour <span className="text-white font-semibold">{artistName}</span></p>

        <div className="mt-3 flex items-center justify-between bg-white/5 rounded-2xl px-4 py-2.5 border border-white/10">
          <div className="flex items-center gap-2">
            <WalletIcon className="w-4 h-4 text-[#00F2FE]" />
            <span className="text-xs text-zinc-400">Mon solde</span>
          </div>
          <span className="font-black text-white text-lg tabular-nums" data-testid="wallet-notes">{Intl.NumberFormat().format(wallet.notes || 0)} Notes</span>
        </div>

        <div className="mt-4 flex gap-2">
          <button onClick={() => setTab("send")} className={`flex-1 py-2 rounded-full text-xs font-bold ${tab === "send" ? "bg-white text-black" : "bg-white/5 text-zinc-300"}`} data-testid="gift-tab-send">Envoyer</button>
          <button onClick={() => setTab("recharge")} className={`flex-1 py-2 rounded-full text-xs font-bold ${tab === "recharge" ? "bg-white text-black" : "bg-white/5 text-zinc-300"}`} data-testid="gift-tab-recharge">Recharger</button>
        </div>

        {!catalog && <div className="py-10 text-center"><Loader2 className="w-6 h-6 text-zinc-500 animate-spin mx-auto" /></div>}

        {catalog && tab === "send" && (
          <div className="mt-4 grid grid-cols-2 gap-2">
            {catalog.gifts.map((g) => {
              const Icon = ICONS[g.id] || Gift;
              const enough = (wallet.notes || 0) >= g.notes;
              return (
                <button
                  key={g.id}
                  onClick={() => sendGift(g)}
                  disabled={busy}
                  className={`flex flex-col items-center gap-1 p-3 rounded-2xl border transition ${enough ? "bg-white/5 border-white/10 hover:bg-white/10" : "bg-white/[0.02] border-white/5 opacity-50"} disabled:opacity-40`}
                  data-testid={`gift-${g.id}`}
                >
                  <div className="text-3xl">{g.emoji}</div>
                  <Icon className="w-3 h-3 text-zinc-400" />
                  <div className="text-xs text-white font-bold">{g.label}</div>
                  <div className="text-[10px] text-[#FFD60A] font-black">{g.notes} Notes</div>
                </button>
              );
            })}
          </div>
        )}

        {catalog && tab === "recharge" && (
          <div className="mt-4 space-y-2">
            {bundles.map((b) => (
              <button
                key={b.id}
                onClick={() => buyBundle(b)}
                disabled={busy}
                className="w-full flex items-center justify-between p-3 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 disabled:opacity-40"
                data-testid={`bundle-${b.id}`}
              >
                <div className="text-left">
                  <div className="text-sm font-bold text-white">{b.label}</div>
                  <div className="text-[11px] text-zinc-400">{Intl.NumberFormat().format(b.notes)} Notes{b.bonus ? ` +${b.bonus} bonus` : ""}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-black text-[#00F2FE]">{Intl.NumberFormat().format(b.price)} FCFA</div>
                </div>
              </button>
            ))}
            <p className="text-[10px] text-zinc-500 mt-3 text-center">Sandbox — paiement auto-confirmé. Brancher Stripe / Flutterwave en prod.</p>
          </div>
        )}
      </div>
    </div>
  );
}
